import { createContext, useContext } from 'react';
import { Outlet, useLocation, NavLink } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Package, Settings2 } from 'lucide-react';

import { useWarehouseId } from '@/contexts/WarehouseContext';
import { SafetyStockApplyProgressModal } from './SafetyStockApplyProgressModal';
import { useSafetyStockApplyFlow } from '../hooks/useSafetyStockApplyFlow';

const TAB_ROUTES = [
  { path: 'resumen', labelKey: 'safetyStock.tabs.summary', icon: BarChart3 },
  { path: 'parametros', labelKey: 'safetyStock.tabs.parameters', icon: Settings2 },
  { path: 'productos', labelKey: 'safetyStock.tabs.products', icon: Package },
  // Recálculos oculto de momento (ruta redirige a resumen).
] as const;

function tabClass(isActive: boolean): string {
  return [
    'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'border-stocker-primary text-stocker-primary'
      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900',
  ].join(' ');
}

type ApplyFlowApi = ReturnType<typeof useSafetyStockApplyFlow>;

const SafetyStockApplyContext = createContext<ApplyFlowApi | null>(null);

export function useSafetyStockApply() {
  const ctx = useContext(SafetyStockApplyContext);
  if (!ctx) {
    throw new Error('useSafetyStockApply must be used within SafetyStockModuleLayout');
  }
  return ctx;
}

export function SafetyStockModuleLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const basePath = '/safety-stock';
  const warehouseId = useWarehouseId();
  const applyFlow = useSafetyStockApplyFlow({ warehouseId });

  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return (
    <SafetyStockApplyContext.Provider value={applyFlow}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 shadow-sm md:px-6">
          <nav className="-mb-px flex flex-wrap gap-1" aria-label={t('safetyStock.tabsAria')}>
            {TAB_ROUTES.map(({ path, labelKey, icon: Icon }) => (
              <NavLink
                key={path}
                to={`${basePath}/${path}`}
                className={({ isActive }) => tabClass(isActive)}
                end={path === 'resumen'}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {t(labelKey)}
              </NavLink>
            ))}
          </nav>
        </div>
        <div ref={panelRef} className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          {typeof warehouseId !== 'number' ? (
            <div className="flex flex-col items-center justify-center gap-2 p-10 text-center">
              <p className="text-base font-medium text-gray-900">
                {t('safetyStock.selectWarehouse')}
              </p>
              <p className="max-w-md text-sm text-gray-500">
                {t('safetyStock.selectWarehouseHint')}
              </p>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
      <SafetyStockApplyProgressModal
        open={applyFlow.modalOpen}
        mode={applyFlow.modalMode}
        isRunning={applyFlow.isRunning}
        flowComplete={applyFlow.flowComplete}
        errorMessage={applyFlow.errorMessage}
        runStatus={applyFlow.runStatus}
        reattached={applyFlow.reattached}
        confirmItemCount={applyFlow.confirmItemCount}
        onConfirm={() => void applyFlow.confirmApply()}
        onClose={applyFlow.closeModal}
        onRetry={() => void applyFlow.retryApply()}
      />
    </SafetyStockApplyContext.Provider>
  );
}
